import axios from 'axios'
import type { AxiosResponse, AxiosError } from 'axios'
import { API_BASE_URL } from '../utils/constants'
import { logger } from '../utils/logger'

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor for adding auth token and logging
api.interceptors.request.use(
  (config) => {
    // Log API request in development
    logger.apiRequest(
      config.method?.toUpperCase() || 'UNKNOWN',
      config.url || 'unknown',
      config.data
    )
    
    // Get token from localStorage (persistent storage)
    try {
      const storedState = localStorage.getItem('bgai-auth-storage')
      const directToken = localStorage.getItem('token') // Also check direct token
      const adminToken = localStorage.getItem('adminToken') // Check admin token
      const adminStorage = localStorage.getItem('admin-storage') // Check admin storage
      
      logger.debug('🔍 Token check for request', {
        component: 'API',
        url: config.url,
        hasStoredState: !!storedState,
        hasDirectToken: !!directToken,
        hasAdminToken: !!adminToken,
        hasAdminStorage: !!adminStorage,
        localStorage: {
          'bgai-auth-storage': storedState ? 'present' : 'missing',
          'token': directToken ? 'present' : 'missing',
          'adminToken': adminToken ? 'present' : 'missing',
          'admin-storage': adminStorage ? 'present' : 'missing',
          'user_type': localStorage.getItem('user_type')
        }
      })
      
      if (storedState) {
        const parsed = JSON.parse(storedState)
        if (parsed.state?.token) {
          config.headers.Authorization = `Bearer ${parsed.state.token}`
          logger.debug('✅ JWT token attached from bgai-auth-storage', {
            component: 'API',
            action: 'token_attached',
            url: config.url,
            tokenPreview: parsed.state.token.substring(0, 20) + '...'
          })
        }
      } else if (adminToken) {
        // Admin token direct
        config.headers.Authorization = `Bearer ${adminToken}`
        logger.debug('✅ JWT token attached from adminToken', {
          component: 'API',
          action: 'token_attached_admin',
          url: config.url,
          tokenPreview: adminToken.substring(0, 20) + '...'
        })
      } else if (adminStorage) {
        // Try admin storage
        try {
          const parsed = JSON.parse(adminStorage)
          if (parsed.token || parsed.access_token) {
            const token = parsed.token || parsed.access_token
            config.headers.Authorization = `Bearer ${token}`
            logger.debug('✅ JWT token attached from admin-storage', {
              component: 'API',
              action: 'token_attached_admin_storage',
              url: config.url,
              tokenPreview: token.substring(0, 20) + '...'
            })
          }
        } catch (adminParseError) {
          logger.warn('Failed to parse admin-storage', { error: adminParseError })
        }
      } else if (directToken) {
        // Fallback to direct token
        config.headers.Authorization = `Bearer ${directToken}`
        logger.debug('✅ JWT token attached from direct localStorage', {
          component: 'API',
          action: 'token_attached_direct',
          url: config.url,
          tokenPreview: directToken.substring(0, 20) + '...'
        })
      } else {
        logger.warn('⚠️ No token found for request', {
          component: 'API',
          url: config.url,
          action: 'no_token'
        })
      }
    } catch (error) {
      logger.warn('Failed to parse stored auth state for request', {
        component: 'API',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    }
    
    // Note: If no token in localStorage, the request will proceed without auth
    // and should get 401/403, which will trigger logout
    
    return config
  },
  (error: AxiosError) => {
    logger.error('API request failed', {
      component: 'API',
      error: error.message,
      action: 'request_interceptor'
    })
    return Promise.reject(error)
  }
)

// Response interceptor for handling common errors and logging
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log successful API response
    logger.apiResponse(
      response.config.method?.toUpperCase() || 'UNKNOWN',
      response.config.url || 'unknown',
      response.status,
      response.data
    )
    
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any

    // Log API error
    logger.apiResponse(
      error.config?.method?.toUpperCase() || 'UNKNOWN',
      error.config?.url || 'unknown',
      error.response?.status || 0,
      error.response?.data
    )

    // Handle 401 errors (unauthorized - expired JWT) and some 403 errors
    // Note: Not all 403s are auth failures - some are permission denials for specific actions
    // Skip auth endpoints — their 401s mean "wrong credentials", not "expired token"
    const requestUrl = originalRequest?.url || ''
    const isAuthEndpoint = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register')
    const isAuthFailure = !isAuthEndpoint && (
                          error.response?.status === 401 ||
                          (error.response?.status === 403 &&
                           (error.response?.data?.detail?.includes('token') ||
                            error.response?.data?.detail?.includes('authentication') ||
                            error.response?.data?.detail?.includes('unauthorized'))))

    if (isAuthFailure && !originalRequest._retry) {
      originalRequest._retry = true

      logger.warn('JWT token expired or invalid, logging out user', {
        component: 'API',
        status: error.response?.status,
        url: originalRequest.url,
        action: 'token_expired'
      })

      try {
        // Clear localStorage auth data directly
        localStorage.removeItem('bgai-auth-storage')
        delete api.defaults.headers.common['Authorization']
        
        // Only redirect if not already on auth pages
        const currentPath = window.location.pathname
        if (!currentPath.startsWith('/auth/')) {
          logger.info('Redirecting to login due to expired token', {
            component: 'API',
            action: 'redirect_to_login',
            fromPath: currentPath
          })
          
          // Redirect to login page
          window.location.href = '/auth/login'
        }
        
        return Promise.reject(new Error('Authentication expired. Please login again.'))
      } catch (logoutError) {
        logger.error('Error during logout process', {
          component: 'API',
          error: logoutError instanceof Error ? logoutError.message : 'Unknown error'
        })
        
        // Force redirect even if logout fails
        window.location.href = '/auth/login'
        return Promise.reject(new Error('Authentication expired. Please login again.'))
      }
    }

    // Handle network errors
    if (!error.response) {
      const networkError = new Error('Network error. Please check your connection.')
      return Promise.reject(networkError)
    }

    // Handle specific error status codes
    const errorMessage = error.response?.data?.detail || 
                        error.response?.data?.message || 
                        error.message || 
                        'An unexpected error occurred'

    // Create a standardized error object
    const standardError = new Error(errorMessage)
    ;(standardError as any).status = error.response?.status
    ;(standardError as any).data = error.response?.data

    // Emit limit reached event for UI toast/upgrade prompts
    try {
      const data: any = error.response?.data
      if (data?.code === 'limit_reached') {
        const { emitLimitReached } = await import('../utils/limitEvents')
        emitLimitReached({
          metric: data.metric,
          used: data.used,
          limit: data.limit,
          plan_name: data.plan_name
        })
      }
    } catch {}

    return Promise.reject(standardError)
  }
)

// Generic API response type
export interface ApiResponse<T = any> {
  data: T
  message?: string
  success: boolean
}

// Helper functions for common HTTP methods
export const apiHelpers = {
  get: <T>(url: string, config?: any) => api.get<T>(url, config),
  post: <T>(url: string, data?: any, config?: any) => api.post<T>(url, data, config),
  put: <T>(url: string, data?: any, config?: any) => api.put<T>(url, data, config),
  patch: <T>(url: string, data?: any, config?: any) => api.patch<T>(url, data, config),
  delete: <T>(url: string, config?: any) => api.delete<T>(url, config)
}

// ====================================
// ADMIN API CLIENT
// ====================================
// Dedicated axios instance for admin endpoints.
// Always sends the admin JWT token from localStorage('adminToken').
// Use this instead of `api` for any /admin/* endpoint.

export const adminApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

adminApiClient.interceptors.request.use(
  (config) => {
    const adminToken = localStorage.getItem('adminToken')
    if (adminToken && config.headers) {
      config.headers.Authorization = `Bearer ${adminToken}`
    }
    logger.apiRequest(
      config.method?.toUpperCase() || 'UNKNOWN',
      config.url || 'unknown',
      config.data
    )
    return config
  },
  (error: AxiosError) => Promise.reject(error)
)

adminApiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    logger.apiResponse(
      response.config.method?.toUpperCase() || 'UNKNOWN',
      response.config.url || 'unknown',
      response.status,
      response.data
    )
    return response
  },
  (error: AxiosError) => {
    logger.apiResponse(
      error.config?.method?.toUpperCase() || 'UNKNOWN',
      error.config?.url || 'unknown',
      error.response?.status || 0,
      error.response?.data
    )
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken')
      const currentPath = window.location.pathname
      if (currentPath.startsWith('/admin') && !currentPath.includes('/login')) {
        window.location.href = '/admin/login'
      }
    }
    const errorMessage = (error.response?.data as any)?.detail || error.message || 'An unexpected error occurred'
    const standardError = new Error(errorMessage)
    ;(standardError as any).status = error.response?.status
    ;(standardError as any).data = error.response?.data
    return Promise.reject(standardError)
  }
)

export default api
